(() => {
  if (customElements.get('ct-offer-timer')) return;

  class CtOfferTimer extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.content = this.querySelector('[data-ct-offer-content]');
      this.timeElement = this.querySelector('[data-ct-offer-time]');
      this.unitElement = this.querySelector('[data-ct-offer-unit]');
      this.expiredElement = this.querySelector('[data-ct-offer-expired]');
      this.dial = this.querySelector('[role="timer"]');
      this.mode = this.dataset.mode === 'fixed' ? 'fixed' : 'session';
      this.durationMs = Math.max(60, Number(this.dataset.durationSeconds) || 900) * 1000;
      this.hideExpired = this.dataset.hideExpired !== 'false';
      this.hasOffer = this.dataset.hasOffer === 'true';
      this.inViewport = !('IntersectionObserver' in window);
      this.expired = false;

      this.onVisibilityChange = () => {
        this.renderTime();
        this.syncTimer();
      };
      this.onVariantChange = this.onVariantChange.bind(this);

      document.addEventListener('visibilitychange', this.onVisibilityChange);

      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(
          ([entry]) => {
            this.inViewport = Boolean(entry?.isIntersecting);
            if (this.inViewport) this.renderTime();
            this.syncTimer();
          },
          { threshold: 0.05 }
        );
        this.observer.observe(this);
      }

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribeVariant = subscribe(PUB_SUB_EVENTS.variantChange, this.onVariantChange);
      }

      if (this.hasOffer) this.ensureDeadline();
      this.renderTime();
      this.syncVisibility();
      this.syncTimer();
    }

    disconnectedCallback() {
      window.clearTimeout(this.timerId);
      this.observer?.disconnect();
      this.unsubscribeVariant?.();
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
      this.initialized = false;
    }

    ensureDeadline() {
      if (Number.isFinite(this.deadline)) return;

      if (this.mode === 'fixed') {
        const fixedDeadline = Date.parse(this.dataset.fixedEnd || '');
        this.deadline = Number.isFinite(fixedDeadline) ? fixedDeadline : 0;
        return;
      }

      let storedDeadline = 0;
      try {
        storedDeadline = Number(sessionStorage.getItem(this.dataset.storageKey));
      } catch (_error) {
        storedDeadline = 0;
      }

      if (Number.isFinite(storedDeadline) && storedDeadline > 0) {
        this.deadline = storedDeadline;
        return;
      }

      this.deadline = Date.now() + this.durationMs;
      try {
        sessionStorage.setItem(this.dataset.storageKey, String(this.deadline));
      } catch (_error) {
        // The in-memory deadline still prevents a restart during this page view.
      }
    }

    onVariantChange(event) {
      const sourceDocument = event?.data?.html;
      const source = sourceDocument?.getElementById?.(this.id);
      if (!source) return;

      this.hasOffer = source.dataset.hasOffer === 'true';
      this.dataset.hasOffer = String(this.hasOffer);
      this.copyText(source, '[data-ct-offer-badge]');
      this.copyText(source, '[data-ct-offer-savings]');

      if (this.hasOffer) this.ensureDeadline();
      this.renderTime();
      this.syncVisibility();
      this.syncTimer();
    }

    copyText(source, selector) {
      const destinationNode = this.querySelector(selector);
      const sourceNode = source.querySelector(selector);
      if (destinationNode && sourceNode) destinationNode.textContent = sourceNode.textContent;
    }

    renderTime() {
      if (!this.hasOffer || !this.timeElement) return;
      this.ensureDeadline();

      const remainingMs = Math.max(0, this.deadline - Date.now());
      if (remainingMs <= 0) {
        this.finish();
        return;
      }

      const totalSeconds = Math.ceil(remainingMs / 1000);
      const hours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;
      const display =
        hours > 0
          ? `${hours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
          : `${minutes}:${String(seconds).padStart(2, '0')}`;

      this.timeElement.textContent = display;
      if (this.unitElement) this.unitElement.textContent = hours > 0 ? 'HRS' : 'MIN';
      const accessibleTime = this.dataset.visualSession === 'true'
        ? `Contador visual de la sesión: ${display}`
        : `Quedan ${display} para que finalice la oferta`;
      this.dial?.setAttribute('aria-label', accessibleTime);

      const progress = Math.max(0, Math.min(100, (remainingMs / this.durationMs) * 100));
      this.style.setProperty('--ct-timer-progress', `${progress}%`);
    }

    finish() {
      if (this.expired) return;
      this.expired = true;
      window.clearTimeout(this.timerId);
      this.timerId = undefined;

      if (this.hideExpired) {
        this.hidden = true;
        return;
      }

      if (this.content) this.content.hidden = true;
      if (this.expiredElement) {
        this.expiredElement.textContent = this.dataset.expiredText || 'Esta promoción finalizó.';
        this.expiredElement.hidden = false;
      }
    }

    syncVisibility() {
      if (this.expired) {
        this.hidden = this.hideExpired;
        return;
      }
      this.hidden = !this.hasOffer;
    }

    syncTimer() {
      window.clearTimeout(this.timerId);
      this.timerId = undefined;

      if (!this.hasOffer || this.expired || !this.inViewport || document.hidden) return;
      const delay = 1025 - (Date.now() % 1000);
      this.timerId = window.setTimeout(() => {
        this.renderTime();
        this.syncTimer();
      }, delay);
    }
  }

  customElements.define('ct-offer-timer', CtOfferTimer);
})();
